import { useMemo } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

// Hoisted so every consumer passes the same options object identity to the hook.
const PRICE_ALERTS_FLAG_OPTIONS = { consentKey: 'accommodationsPriceAlerts' } as const;

/**
 * The single gate for every price-alerts surface (the accommodations banner, the two Guest Guide
 * actions, and the reminder-modal trigger).
 *
 * `accommodationsPriceAlerts` is both an Amplitude experiment and a `UserConsentKey` of the same
 * name, so one `useIsFeatureEnabled` call resolves both: the per-event consent override wins when
 * set (`true` → treatment, `false` → control) and otherwise the Amplitude assignment decides.
 *
 * This is a straight on/off gate with no day-range logic: treatment shows every surface at all
 * horizons, control hides every surface at all horizons.
 *
 * Returns `false` while remote flags are still loading, so no surface flashes in before the
 * per-event override is known.
 *
 * @param options.skip When `true`, the Amplitude flag is not evaluated and no experiment
 *   exposure is logged. Guest-side callers should pass `skip: !geoSupported` so non-US
 *   guests never pollute the experiment. Admin callers can omit `skip` entirely.
 */
export const usePriceAlertsActive = (options?: Readonly<{ skip?: boolean }>): boolean => {
  const skip = options?.skip;
  const flagOptions = useMemo(() => (skip ? { ...PRICE_ALERTS_FLAG_OPTIONS, skip: true } : PRICE_ALERTS_FLAG_OPTIONS), [skip]);
  const [isEnabled] = useIsFeatureEnabled('accommodationsPriceAlerts', flagOptions);

  return isEnabled;
};

/**
 * Like {@link usePriceAlertsActive} but also exposes whether the flag has finished
 * resolving. Callers that gate a *competing* experiment (e.g. the reminder modal)
 * should wait until this settles before evaluating their own flag — otherwise the
 * initial `false` while loading causes a premature experiment exposure.
 */
export const usePriceAlertsActiveWithLoading = (options?: Readonly<{ skip?: boolean }>): readonly [isActive: boolean, isLoading: boolean] => {
  const skip = options?.skip;
  const flagOptions = useMemo(() => (skip ? { ...PRICE_ALERTS_FLAG_OPTIONS, skip: true } : PRICE_ALERTS_FLAG_OPTIONS), [skip]);
  const [isEnabled, isLoading] = useIsFeatureEnabled('accommodationsPriceAlerts', flagOptions);

  return [isEnabled, isLoading] as const;
};
